import { Drawer } from 'antd';
import { FC, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { SessionUserI } from '~src/interfaces/session.types';
import { StoreI } from '~src/rootReducer';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import Dialog from '~src/shared/dialogs/dialog/Dialog';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { ObjectiveItem, ObjectiveStatus } from '~src/store/okrs/interfaces';
import { createKeyResult, fetchKeyResults } from '~src/store/okrs/keyResults/actions';
import { KeyResultI } from '~src/store/okrs/keyResultStream/interfaces';
import AddKeyResultForm from '~src/modules/okrs/manageObjectives/create/keyResults/AddKeyResultForm';
import { KeyResultAdd } from '~src/modules/okrs/manageObjectives/create/keyResults/CreateKeyResults';
import { FormWrapper } from '~src/modules/okrs/manageObjectives/create/objectiveAddForm.sc';
import KeyResultsListWrapper from '~src/modules/okrs/manageObjectives/shared/keyResults/KeyResultsListWrapper';
import { LoadingWrapper } from '~src/modules/okrs/manageObjectives/shared/shared.sc';
import { ShowNotification } from '~src/store/app/interfaces';
import { showNotification } from '~src/store/app/actions';
import { useNotification } from '~src/services/hooks/useNotification';

import EditKeyResultItem from './EditKeyResultItem';
import KeyResultDetails from './KeyResultDetails';
import { getContentLocale } from '~src/services/session';
import { SortingValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import { getLocationState, setLocationState } from '~src/utils';
import {canEditKeyResult} from "~src/store/okrs/keyResultStream/permissions";
import logger from "~src/services/logger/LlsLogger";

interface OwnProps {
  objective: ObjectiveItem;
  frozen: boolean;
  onUpdateKeyResult: () => void;
}

interface StateProps {
  loading: boolean;
  sessionUser: SessionUserI | null;
  loadingCreation: boolean;
  keyResults?: KeyResultI[];
  contentLocale: string;
}

interface DispatchProps {
  fetchKeyResults: (name: string, queryParams?: Record<string, string>) => ApiReduxResp<KeyResultI[]>;
  createKeyResult: (objectiveId: string, keyResult: KeyResultAdd) => ApiReduxResp<KeyResultI>;
  showNotification: ShowNotification;
}

type Props = StateProps & DispatchProps & OwnProps;

const EditKeyResultList: FC<Props> = (props) => {
  const [addForm, setAddForm] = useState(false);
  const { notificationsFromAPIErrors } = useNotification();

  const showAddForm = () => setAddForm(true);
  const hideAddForm = () => setAddForm(false);

  const [selectedKeyResult, setSelectedKeyResult] = useState<KeyResultI | null>(null);

  useEffect(() => {
    if (selectedKeyResult && props.keyResults) {
      const keyResultInList = props.keyResults.find((inList) => inList.id === selectedKeyResult.id);
      if (keyResultInList) {
        setSelectedKeyResult(keyResultInList);
      }
    }
  }, [props.keyResults]);

  const resetSelectedKeyResult = () => setSelectedKeyResult(null);

  const [localLoading, setLocalLoading] = useState(true);
  const locationSort = (getLocationState().sort as SortingValues) || null;
  const [sort, setSort] = useState<string>(locationSort || SortingValues.Created_desc);

  const sessionUserCanEdit = (keyResultItem: KeyResultI) => {
    if (!props.sessionUser) {
      return false;
    }
    return canEditKeyResult(props.sessionUser, props.objective, keyResultItem);
  };

  useEffect(() => {
    void props.fetchKeyResults(props.objective.id, { sort }).finally(() => {
      setLocalLoading(false);
    });
  }, [sort]);

  const submitCreation = (keyResult: KeyResultAdd) => {
    void props.createKeyResult(props.objective.id, keyResult).then((rsp) => {
      if (rsp.error && rsp.error.response.status >= 400 && rsp.error.response.status !== 422) {
        notificationsFromAPIErrors(rsp.error.response.data, false, 'x.okr.error');
        return;
      }
      hideAddForm();
      props.onUpdateKeyResult();
    });
  };

  const handleSortKeyResults = useCallback((sort: string) => {
    setSort(sort);
    setLocationState({ sort });
  }, []);

  return (
    <div>
      <Dialog show={addForm} onHide={hideAddForm}>
        <FormWrapper>
          {addForm && (
            <AddKeyResultForm
              loadingAdd={props.loadingCreation}
              addKeyResult={submitCreation}
              hide={hideAddForm}
            />
          )}
        </FormWrapper>
      </Dialog>
      <Drawer
        width={(window.innerWidth - 1170) / 2 + 1170 * 0.33}
        onClose={resetSelectedKeyResult}
        open={!!selectedKeyResult}
      >
        {selectedKeyResult && (
          <KeyResultDetails
            sessionUser={props.sessionUser}
            keyResult={selectedKeyResult}
            onUpdateKeyResult={props.onUpdateKeyResult}
            objective={props.objective}
            close={resetSelectedKeyResult}
          />
        )}
      </Drawer>
      {props.loading || localLoading ? (
        <LoadingWrapper>
          <LoadingSpinner alignCls="center" />
        </LoadingWrapper>
      ) : (
        <KeyResultsListWrapper
          hasContent={props.keyResults ? props.keyResults.length > 0 : false}
          trigger={showAddForm}
          readonly={props.frozen || props.objective.status === ObjectiveStatus.CLOSED}
          onSort={handleSortKeyResults}
          sort={sort}
        >
          {props.keyResults &&
            props.keyResults.map((keyResultItem) => {
              if (!keyResultItem) {
                logger.warn('EditKeyResultList received sparse keyResult list, ignoring empty item');
                return null;
              }
              return (
                <EditKeyResultItem
                  canEdit={sessionUserCanEdit(keyResultItem)}
                  key={keyResultItem.id}
                  keyResult={keyResultItem}
                  onSelect={() => setSelectedKeyResult(keyResultItem)}
                  isSelected={Boolean(selectedKeyResult && selectedKeyResult.id === keyResultItem.id)}
                  onUpdateKeyResult={props.onUpdateKeyResult}
                />
              );
            })}
        </KeyResultsListWrapper>
      )}
    </div>
  );
};

const mapStateToProps = (state: StoreI): StateProps => ({
  loading: state.okrs.keyResults.loadingKeyResults,
  loadingCreation: state.okrs.keyResults.loadingCreation,
  sessionUser: state.user.session.user,
  keyResults: state.okrs.keyResults.keyResults,
  contentLocale: getContentLocale(state),
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchKeyResults: (objectiveId, queryParams) => dispatch(fetchKeyResults(objectiveId, queryParams)),
  createKeyResult: (objectiveId, keyResult) => dispatch(createKeyResult(objectiveId, keyResult)),
  showNotification: (notification) => dispatch(showNotification(notification)),
});

const EditKeyResultListConnected = connect(mapStateToProps, mapDispatchToProps)(EditKeyResultList);

export default EditKeyResultListConnected;
